import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loading, EmptyState, Money } from "@/components/common";
import { STATUS_META } from "@/components/common";
import { BarChart3 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
} from "recharts";

export default function Reports() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/reports").then((x) => setR(x.data)).catch(() => setR(false)); }, []);
  if (r === null) return <Loading />;
  if (!r) return <EmptyState title="Unable to load reports" />;

  const statusColors = { new: "#3b82f6", confirmed: "#6366f1", in_progress: "#f59e0b", running: "#0ea5e9", completed: "#10b981", cancelled: "#f43f5e" };
  const chartData = Object.entries(r.orders_by_status).map(([k, v]) => ({ name: STATUS_META[k]?.label || k, value: v, key: k }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-display text-slate-900">Reports</h1>
        <p className="text-sm text-slate-500 mt-1">Business summary at a glance.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="Revenue Collected" value={<Money value={r.revenue_collected} />} />
        <Metric label="Pending Payments" value={<Money value={r.pending_payments} />} />
        <Metric label="Completed Orders" value={r.completed_orders} />
        <Metric label="Active Campaigns" value={r.active_campaigns} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border rounded-xl shadow-sm p-5">
          <h3 className="font-semibold font-display text-slate-900 mb-4">Orders by Status</h3>
          {r.total_orders === 0 ? <EmptyState icon={BarChart3} title="No orders yet" /> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ left: -20 }}>
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {chartData.map((e) => <Cell key={e.key} fill={statusColors[e.key] || "#3b82f6"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border rounded-xl shadow-sm p-5">
          <h3 className="font-semibold font-display text-slate-900 mb-4">Status Breakdown</h3>
          <div className="space-y-3">
            {chartData.map((e) => (
              <div key={e.key} className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: statusColors[e.key] }} /> {e.name}
                </span>
                <span className="font-semibold text-slate-900">{e.value}</span>
              </div>
            ))}
            <div className="pt-3 border-t flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Total Orders</span>
              <span className="font-bold text-slate-900">{r.total_orders}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-bold font-display text-slate-900">{value}</div>
    </div>
  );
}
